/**
 * Iter-194 — Which door does a quote line belong to?
 *
 * Line items normally carry `door_reference` / `door_id`. Some lines
 * arrive without them (an "extra job" added with no door picked, an
 * older edit screen that dropped the metadata, an Ease & Adjust line
 * whose only door hint is "… — RAF-FIRST-STAI-004" in its text). Those
 * used to fall under "Additional Works" on the PDF even though the text
 * plainly names the door. This helper resolves the door key the same way
 * everywhere (PDF layouts, portal, quote screen):
 *
 *   1. explicit `door_reference` / `door_id`
 *   2. a "Door: <ref>" prefix in the description (generator style)
 *   3. the text mentions one of the door references already on the quote
 *      (longest reference first, whole-token match, case-insensitive)
 */

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Distinct door references (and ids when no reference) present on the quote. */
export const knownDoorRefs = (items: any[]): string[] => {
  const out: string[] = [];
  (items || []).forEach((it: any) => {
    const ref = String(it?.door_reference || '').trim();
    if (ref && !out.includes(ref)) out.push(ref);
  });
  // Longest first so "FLAT-12" wins over "FLAT-1" when both exist.
  return out.sort((a, b) => b.length - a.length);
};

/** Door reference mentioned in the line's text, if it matches a known door. */
export const inferDoorRefFromText = (it: any, refs: string[]): string => {
  if (!refs || refs.length === 0) return '';
  const text = `${it?.component || ''} ${it?.description || ''}`;
  if (!text.trim()) return '';
  for (const ref of refs) {
    if (ref.length < 2) continue;
    const re = new RegExp(`(^|[^A-Za-z0-9])${escapeRe(ref)}(?![A-Za-z0-9])`, 'i');
    if (re.test(text)) return ref;
  }
  return '';
};

/** The bucket key for a line: explicit door, "Door:" prefix, or text mention.
 *  A line the admin filed as extra work ("No door") never infers a door. */
export const quoteItemDoorKey = (it: any, refs?: string[]): string => {
  const direct = it?.door_reference || it?.door_id;
  if (direct) return String(direct);
  if (it?.extra_work) return '';
  const m = String(it?.description || '').match(/^\s*Door:\s*([^·.\n]+)/i);
  if (m && m[1].trim()) return m[1].trim();
  return refs ? inferDoorRefFromText(it, refs) : '';
};

/**
 * Items with door metadata filled in from a sibling line on the same door
 * whenever the line itself has none but its text names the door. Used by
 * screens that read `door_reference` / `door_id` off each line directly.
 * Extra-work lines (`extra_work: true`) are left untouched.
 */
export const healItemDoors = <T extends Record<string, any>>(items: T[]): T[] => {
  const list = Array.isArray(items) ? items : [];
  const refs = knownDoorRefs(list);
  const byRef: Record<string, any> = {};
  list.forEach((it) => {
    const ref = String(it?.door_reference || '').trim();
    if (ref && !byRef[ref]) byRef[ref] = it;
  });
  return list.map((it) => {
    if (it?.door_reference || it?.door_id || it?.extra_work) return it;
    const ref = quoteItemDoorKey(it, refs);
    if (!ref) return it;
    const sib = byRef[ref];
    return {
      ...it,
      // A "Door: X" prefix with no sibling carrying X still names the door.
      door_reference: sib?.door_reference || ref,
      door_id: it.door_id || sib?.door_id || null,
      door_location: it.door_location || sib?.door_location || null,
      door_type: it.door_type || sib?.door_type || null,
      _door_inferred: true,
    };
  });
};
